import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FixedSizeList, ListChildComponentProps } from 'react-window';
import AutoSizer, { Size } from 'react-virtualized-auto-sizer';
import { ButtonV2 } from '@praktio/common-ui';

import { RootState } from '../../../../ducks/store';
import { getUsers } from '../../../../ducks/users';
import { highlightMatch } from '../../../../utils/text';
import NavPanelSkeleton from '../NavPanelSkeleton';
import PanelSearch from '../PanelSearch';
import useSharedTooltip from '../hooks/useSharedTooltip';

const ITEM_HEIGHT = 36;

interface IProps {
  visible: boolean;
}

interface UserRowData {
  users: IUser[];
  currentUserId: string | undefined;
  searchQuery: string;
  onNavigate: (userId: string) => void;
  showTooltip: (e: React.MouseEvent, text: string) => void;
  hideTooltip: () => void;
}

const UserRow: FC<ListChildComponentProps<UserRowData>> = ({ index, style, data }) => {
  const { users, currentUserId, searchQuery, onNavigate, showTooltip, hideTooltip } = data;
  const user = users[index];
  const isActive = currentUserId === user._id;
  const displayName = [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email || '';

  return (
    <div style={style}>
      <div
        className={classNames('NavPanel-item', {
          'NavPanel-item--active': isActive,
        })}
        role="listitem"
        onClick={() => onNavigate(user._id!)}
        onMouseEnter={(e) => showTooltip(e, displayName)}
        onMouseLeave={hideTooltip}
      >
        <span className="NavPanel-item-text">{highlightMatch(displayName, searchQuery)}</span>
      </div>
    </div>
  );
};

const UsersPanel: FC<IProps> = ({ visible }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const currentUserId = searchParams.get('userId') || undefined;
  const unsortedUsers = useSelector((state: RootState) => state.users.list);
  const users = useMemo(
    () =>
      unsortedUsers?.slice().sort((a, b) => {
        const nameA = [a.firstName, a.lastName].filter(Boolean).join(' ') || a.email || '';
        const nameB = [b.firstName, b.lastName].filter(Boolean).join(' ') || b.email || '';
        return nameA.localeCompare(nameB);
      }),
    [unsortedUsers],
  );
  const [fetchDone, setFetchDone] = useState(() => !!unsortedUsers?.length);

  const query = searchQuery.trim().toLowerCase();
  const filteredUsers = query
    ? users?.filter((u) => {
        const displayName = [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email || '';
        return displayName.toLowerCase().includes(query);
      })
    : users;
  const showNoResults = query && filteredUsers?.length === 0;

  useEffect(() => {
    if (visible && !fetchDone) {
      (async () => {
        await (dispatch as any)(getUsers());
        setFetchDone(true);
      })();
    }
  }, [visible, fetchDone, dispatch]);

  const isLoading = !fetchDone && !users?.length;
  const showEmpty = fetchDone && !users?.length;

  const { showTooltip, hideTooltip, tooltipOverlay } = useSharedTooltip();

  const onNavigate = useCallback(
    (userId: string) => navigate(`/admin/users?userId=${userId}`),
    [navigate],
  );

  const hasItems = !!filteredUsers?.length;

  const itemData: UserRowData | null = useMemo(
    () =>
      filteredUsers
        ? { users: filteredUsers, currentUserId, searchQuery, onNavigate, showTooltip, hideTooltip }
        : null,
    [filteredUsers, currentUserId, searchQuery, onNavigate, showTooltip, hideTooltip],
  );

  return (
    <div className="NavPanel-body">
      <PanelSearch value={searchQuery} onChange={setSearchQuery} placeholder="Search..." />
      {isLoading ? <NavPanelSkeleton /> : null}
      {showEmpty ? <div className="NavPanel-empty">No users found</div> : null}
      {showNoResults ? <div className="PanelSearch-empty">No matching users</div> : null}
      {hasItems && itemData ? (
        <div className="NavPanel-list-container" role="list">
          <AutoSizer>
            {({ height, width }: Size) => (
              <FixedSizeList
                className="NavPanel-list dark-scroll"
                height={height}
                width={width}
                itemCount={filteredUsers!.length}
                itemSize={ITEM_HEIGHT}
                itemData={itemData}
              >
                {UserRow}
              </FixedSizeList>
            )}
          </AutoSizer>
        </div>
      ) : null}
      <div className="NavPanel-footer">
        <ButtonV2
          className="NavPanel-footer-btn"
          variant="outline"
          onClick={() => navigate('/admin/users?userId=new')}
        >
          + NEW USER
        </ButtonV2>
      </div>
      {tooltipOverlay}
    </div>
  );
};

export default React.memo(UsersPanel);
